"use client";
import * as React from "react";
import { createPortal } from "react-dom";
import { Button } from "@ui/core";
import { X } from "lucide-react";
import { parseApiError } from "@shared/lib/errors";
import { useTranslation } from "react-i18next";
import { translateUi } from "@i18n/config";

type ToastAction = { label: string; onClick: () => void };
type Toast = {
  id: string;
  message: string;
  description?: string;
  type?: "success" | "error" | "info";
  action?: ToastAction;
  duration?: number;
  preserveOnNavigate?: boolean;
};
type ToastOptions = {
  description?: string;
  action?: ToastAction;
  duration?: number;
  id?: string;
  preserveOnNavigate?: boolean;
};
type Ctx = {
  show: (
    message: string,
    type?: Toast["type"],
    options?: ToastOptions,
  ) => string;
  success: (message: string, options?: ToastOptions) => string;
  error: (message: string, options?: ToastOptions) => string;
  info: (message: string, options?: ToastOptions) => string;
  dismiss: (id: string) => void;
  clearTransient: () => void;
};

const ToastContext = React.createContext<Ctx | undefined>(undefined);
const noopCtx: Ctx = {
  show: () => "",
  success: () => "",
  error: () => "",
  info: () => "",
  dismiss: () => {},
  clearTransient: () => {},
};

const DEFAULT_DURATIONS: Record<NonNullable<Toast["type"]>, number> = {
  success: 3500,
  error: 5000,
  info: 4000,
};
const MAX_VISIBLE_TOASTS = 3;

function getToastSignature(
  message: string,
  type: Toast["type"],
  description?: string,
) {
  return `${type ?? "info"}|${message}|${description ?? ""}`;
}

function limitToastStack(toasts: Toast[], protectedId?: string) {
  if (toasts.length <= MAX_VISIBLE_TOASTS) {
    return { nextToasts: toasts, removedIds: [] as string[] };
  }

  const removeIds = new Set<string>();
  let overflow = toasts.length - MAX_VISIBLE_TOASTS;

  for (const toast of toasts) {
    if (overflow <= 0) break;
    if (toast.id === protectedId || toast.preserveOnNavigate) continue;
    removeIds.add(toast.id);
    overflow -= 1;
  }

  if (overflow > 0) {
    for (const toast of toasts) {
      if (overflow <= 0) break;
      if (toast.id === protectedId || removeIds.has(toast.id)) continue;
      removeIds.add(toast.id);
      overflow -= 1;
    }
  }

  return {
    nextToasts: toasts.filter((toast) => !removeIds.has(toast.id)),
    removedIds: Array.from(removeIds),
  };
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const { t: tUi } = useTranslation("ui");
  const [toasts, setToasts] = React.useState<Toast[]>([]);
  const timersRef = React.useRef<Map<string, number>>(new Map());
  const toastsRef = React.useRef<Toast[]>([]);

  React.useEffect(() => {
    toastsRef.current = toasts;
  }, [toasts]);

  const clearTimer = React.useCallback((id: string) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      window.clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const remove = React.useCallback(
    (id: string) => {
      clearTimer(id);
      setToasts((t) => t.filter((x) => x.id !== id));
    },
    [clearTimer],
  );

  const scheduleRemove = React.useCallback(
    (id: string, duration: number) => {
      clearTimer(id);
      if (duration <= 0) return;
      const timer = window.setTimeout(() => remove(id), duration);
      timersRef.current.set(id, timer);
    },
    [clearTimer, remove],
  );

  const upsertToast = React.useCallback(
    (toast: Omit<Toast, "id"> & { id?: string }) => {
      const type = toast.type ?? "info";
      const signature = getToastSignature(
        toast.message,
        type,
        toast.description,
      );
      const existing = toastsRef.current.find(
        (currentToast) =>
          (toast.id ? currentToast.id === toast.id : false) ||
          getToastSignature(
            currentToast.message,
            currentToast.type ?? "info",
            currentToast.description,
          ) === signature,
      );
      const id =
        existing?.id ??
        toast.id ??
        `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const nextToast: Toast = {
        ...existing,
        ...toast,
        id,
        type,
        preserveOnNavigate:
          toast.preserveOnNavigate ?? existing?.preserveOnNavigate ?? false,
      };
      const duration =
        toast.duration ??
        ((existing?.type ?? "info") === type
          ? existing?.duration
          : undefined) ??
        DEFAULT_DURATIONS[type];

      setToasts((currentToasts) => {
        const nextToasts = currentToasts
          .filter((currentToast) => currentToast.id !== id)
          .concat({ ...nextToast, duration });
        const { nextToasts: limitedToasts, removedIds } = limitToastStack(
          nextToasts,
          id,
        );
        removedIds.forEach((removedId) => clearTimer(removedId));
        return limitedToasts;
      });
      scheduleRemove(id, duration);
      return id;
    },
    [clearTimer, scheduleRemove],
  );

  const show = React.useCallback(
    (
      message: string,
      type: Toast["type"] = "info",
      options: ToastOptions = {},
    ) =>
      upsertToast({
        id: options.id,
        message,
        type,
        description: options.description,
        action: options.action,
        duration: options.duration,
        preserveOnNavigate: options.preserveOnNavigate,
      }),
    [upsertToast],
  );

  const clearTransient = React.useCallback(() => {
    setToasts((currentToasts) => {
      currentToasts.forEach((toast) => {
        if (!toast.preserveOnNavigate) {
          clearTimer(toast.id);
        }
      });
      return currentToasts.filter((toast) => toast.preserveOnNavigate);
    });
  }, [clearTimer]);

  const ctx = React.useMemo<Ctx>(
    () => ({
      show,
      success: (m, options) => show(m, "success", options),
      error: (m, options) => show(m, "error", options),
      info: (m, options) => show(m, "info", options),
      dismiss: remove,
      clearTransient,
    }),
    [show, remove, clearTransient],
  );

  // Bridge for global enqueued toasts (e.g., from SDK error events)
  React.useEffect(() => {
    function drain() {
      try {
        const w = window as any;
        const q: Toast[] = Array.isArray(w.__storizone_toast_queue__)
          ? w.__storizone_toast_queue__
          : [];
        if (q.length) {
          w.__storizone_toast_queue__ = [];
          q.forEach((toast) => {
            upsertToast(toast);
          });
        }
      } catch {}
    }
    document.addEventListener("toast:enqueue", drain);
    drain();
    return () => document.removeEventListener("toast:enqueue", drain);
  }, [upsertToast]);

  React.useEffect(
    () => () => {
      timersRef.current.forEach((timer) => window.clearTimeout(timer));
      timersRef.current.clear();
    },
    [],
  );

  return (
    <ToastContext.Provider value={ctx}>
      {children}
      {typeof document === "undefined"
        ? null
        : createPortal(
            <div
              aria-live="polite"
              className="pointer-events-none fixed bottom-3 right-3 z-[160] flex w-[calc(100%-1.5rem)] max-w-sm flex-col gap-2"
            >
              {toasts.map((t) => (
                <div
                  key={t.id}
                  className={
                    "pointer-events-auto rounded-md border px-3 py-2 text-sm shadow-lg " +
                    (t.type === "success"
                      ? "border-green-300 bg-green-50 text-green-900"
                      : t.type === "error"
                        ? "border-red-300 bg-red-50 text-red-900"
                        : "border-gray-200 bg-white text-gray-900")
                  }
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1 pr-4">
                      <div>{t.message}</div>
                      {t.description ? (
                        <div className="text-xs text-muted-foreground">
                          {t.description}
                        </div>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-2">
                      {t.action ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={t.action.onClick}
                        >
                          {t.action.label}
                        </Button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => remove(t.id)}
                        className="rounded-md p-1 hover:bg-black/5 opacity-70 hover:opacity-100 transition-opacity"
                        aria-label={tUi("close", {
                          defaultValue: "Close",
                        })}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>,
            document.body,
          )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = React.useContext(ToastContext);
  if (!ctx) {
    if (import.meta.env.MODE === "test" || process.env.NODE_ENV === "test")
      return noopCtx;
    throw new Error(
      translateUi(
        "useToastMustBeUsedWithinToastProvider",
        "useToast must be used within ToastProvider",
      ),
    );
  }
  return ctx;
}

// Global HTTP error normalization listener
if (typeof window !== "undefined") {
  // Lazy attach after first tick so it doesn't depend on hooks
  setTimeout(() => {
    try {
      let lastKey = "";
      let lastAt = 0;
      window.addEventListener("http:error", ((e: Event) => {
        const ce = e as CustomEvent<{
          status?: number;
          statusText?: string;
          message?: string;
          correlationId?: string;
          requestId?: string;
          retryable?: boolean;
        }>;
        const d = ce.detail || {};
        const status = d.status || 0;
        const fallback =
          status >= 500
            ? translateUi(
                "serverErrorPleaseTryAgainInAMoment",
                "Server error. Please try again in a moment.",
              )
            : translateUi(
                "connectionLostCheckYourConnectionAndTryAgain",
                "Connection lost. Check your connection and try again.",
              );
        const parsed = parseApiError(
          {
            status,
            statusText: d.statusText,
            message: d.message,
            body: d.message,
            correlationId: d.correlationId || d.requestId,
            retryable: d.retryable,
          },
          fallback,
        );
        const message = parsed.message;

        const key = `${status}:${message}`;
        const now = Date.now();
        if (key === lastKey && now - lastAt < 2000) return; // debounce repeated identical errors
        lastKey = key;
        lastAt = now;

        // Simple retry: reload page to re-run data loaders
        const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const action: ToastAction | undefined = parsed.retryable
          ? {
              label: translateUi("retry", "Retry"),
              onClick: () => {
                try {
                  window.location.reload();
                } catch {}
              },
            }
          : undefined;
        const payload: Toast = {
          id,
          message,
          type: "error",
          action,
          description: parsed.correlationId
            ? translateUi("correlationIdValue0", "Correlation ID: {{value0}}", {
                value0: parsed.correlationId,
              })
            : undefined,
          preserveOnNavigate: status === 0,
        };
        const root = document;
        // Dispatch a custom event to ToastProvider via global state mutation
        // Fallback: queue into a global list if provider not yet mounted
        (window as any).__storizone_toast_queue__ =
          (window as any).__storizone_toast_queue__ || [];
        (window as any).__storizone_toast_queue__.push(payload);
        const ev = new CustomEvent("toast:enqueue");
        root.dispatchEvent(ev);
      }) as any);
    } catch {}
  }, 0);
}
